/**
 * Where each app user is, for the lists — 28 Oct 2026.
 *
 * `skidaCrmCustomerFacts/{uid}` is written by the engine from RevenueCat every
 * six hours: the country the account was last seen in, and the language Skida
 * writes in there. Read by id, thirty at a time, and kept for the page's life
 * — a list re-rendering does not read them again.
 */
import { useEffect, useMemo, useState } from 'react'
import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { isLanguage, languageOfCountry, type Language } from './places'

export interface Place {
  country: string | null
  language: Language | null
}

const NONE: Place = { country: null, language: null }
const cache = new Map<string, Place>()

export async function loadPlaces(uids: string[]): Promise<Map<string, Place>> {
  const want = [...new Set(uids.filter(Boolean))].filter((u) => !cache.has(u))
  for (let i = 0; i < want.length; i += 30) {
    const ids = want.slice(i, i + 30)
    const snap = await getDocs(
      query(collection(db, CRM_COLLECTIONS.customerFacts), where(documentId(), 'in', ids)),
    )
    const found = new Set<string>()
    for (const d of snap.docs) {
      const country = typeof d.get('country') === 'string' && d.get('country') ? String(d.get('country')).toUpperCase() : null
      const stored = d.get('language')
      cache.set(d.id, { country, language: isLanguage(stored) ? stored : languageOfCountry(country) })
      found.add(d.id)
    }
    // Not seen by RevenueCat: a known blank rather than a question asked again.
    for (const id of ids) if (!found.has(id)) cache.set(id, NONE)
  }
  return new Map(uids.map((u) => [u, cache.get(u) ?? NONE]))
}

/** The places of these users; empty until read, and on a failed read. */
export function usePlaces(uids: string[]): Map<string, Place> {
  const key = useMemo(() => [...new Set(uids.filter(Boolean))].sort().join(','), [uids])
  const [places, setPlaces] = useState<Map<string, Place>>(new Map())
  useEffect(() => {
    if (!key) return
    let live = true
    loadPlaces(key.split(','))
      .then((m) => live && setPlaces(m))
      .catch((e: unknown) => console.warn('Could not read where users are', e))
    return () => {
      live = false
    }
  }, [key])
  return places
}

export interface PlaceCounts {
  seen: number
  withCountry: number
  countries: Record<string, number>
  languages: Record<string, number>
}

export interface PlaceDistribution extends PlaceCounts {
  /** Paying subscribers (`live`), counted the same way — 29 Oct 2026. Null until the next sync. */
  subscribers: PlaceCounts | null
  at: Date | null
}

/** The totals the engine keeps beside the facts, for the dashboard card. */
export async function loadPlaceDistribution(): Promise<PlaceDistribution | null> {
  const s = await getDoc(doc(db, CRM_COLLECTIONS.rollupState, 'customerFacts'))
  if (!s.exists()) return null
  const v = s.data()
  return {
    seen: Number(v.seen) || 0,
    withCountry: Number(v.withCountry) || 0,
    countries: (v.countries ?? {}) as Record<string, number>,
    languages: (v.languages ?? {}) as Record<string, number>,
    subscribers: v.subscribers
      ? {
          seen: Number(v.subscribers.seen) || 0,
          withCountry: Number(v.subscribers.withCountry) || 0,
          countries: (v.subscribers.countries ?? {}) as Record<string, number>,
          languages: (v.subscribers.languages ?? {}) as Record<string, number>,
        }
      : null,
    at: v.at?.toDate?.() ?? null,
  }
}
